import type { Umriss } from "../shared/umriss.ts";
import type { BildAusListe } from "./lib/api.ts";
import { alsClipPath, ausschnitt } from "./lib/umriss.ts";

/**
 * Das Bild einer Figur — der Umriss als `clip-path`, die Kachel auf das Hüllrechteck
 * zugeschnitten.
 *
 * Stand bis zum 24.09.2026 in `src/Reihe.tsx`. Seit Schritt 5 zeigt auch der Bildschirm
 * „Bild bearbeiten" dieselbe Figur (`src/Zuordnung.tsx`), und zwei Fassungen desselben
 * Zuschnitts wären genau die Doppelung, aus der später eine Abweichung wird
 * (ARCHITEKTUR.md 2.5): die Figur sähe dort anders aus als in der Reihe.
 *
 * Die Rechnung ist dieselbe wie im Raster aus Schritt 3 (`ausschnitt()`): die Kachel
 * zeigt **die Person**, nicht das Foto um sie herum. Ohne das lag zwischen vier Personen
 * mehr Weißraum als Person — am 24.09.2026 auf Carolins Telefon gesehen.
 */
export default function Figurbild({ bild }: { bild: BildAusListe }) {
  const umriss: Umriss | null = bild.umriss;
  if (!umriss) {
    return (
      <img
        src={bild.vorschau}
        alt=""
        loading="lazy"
        draggable={false}
        className="h-full w-full rounded-lg bg-neutral-100 object-cover"
      />
    );
  }
  const schnitt = ausschnitt(umriss, bild.breite, bild.hoehe);
  return (
    <span className="relative block h-full w-full overflow-hidden">
      <img
        src={bild.vorschau}
        alt=""
        loading="lazy"
        draggable={false}
        className="absolute max-w-none"
        style={{
          width: `${String(schnitt.bildBreite)}%`,
          height: `${String(schnitt.bildHoehe)}%`,
          left: `${String(schnitt.versatzLinks)}%`,
          top: `${String(schnitt.versatzOben)}%`,
          // Prozente rechnen gegen die **Bild**-Box, nicht gegen die Kachel — deshalb
          // gilt derselbe Umriss hier unverändert (ARCHITEKTUR.md 6.2).
          clipPath: alsClipPath(umriss),
        }}
      />
    </span>
  );
}

/** Wie breit eine Figur in einer Box dieser Höhe steht — für die kleinen Vorschauen
 *  neben einem Text, wo die Breite aus dem Hüllrechteck folgt und nicht geraten wird. */
export function figurBreite(bild: BildAusListe, hoehePx: number): number {
  if (!bild.umriss) return hoehePx;
  return hoehePx * ausschnitt(bild.umriss, bild.breite, bild.hoehe).seitenverhaeltnis;
}
